import { Middleware } from '@reduxjs/toolkit';

import {
  changeConfirmationFieldValue,
  fetchCertainPaymentMethodSuccess,
  fetchPaymentInfoSuccess,
  processPaymentFailure,
  registrationPaymentFailure,
  registrationPaymentSuccess,
  setActiveScreen,
  setConfirmationFormData,
  setPaymentInputs,
  setTermsAgreementCheckbox,
} from '@cashier/cashier-data-access';
import {
  DynamicScreens,
  PaymentMethodInput,
  ResultActionTypes,
} from '@cashier/cashier-model';
import {
  eventBus,
  EventFactory,
  PaymentStateChecker,
} from '@cashier/cashier-shared';

import { PreProcessorAttributeFilter } from '../helpers';

export const platformProcessingInitiatorMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);

    const {
      cashierConfiguration: { activeType },
      paymentProcessing: { result: paymentProcessingResult },
      paymentRegistration: { resultAction: paymentRegistrationResultAction },
    } = store.getState();

    if (action.type === fetchCertainPaymentMethodSuccess.toString()) {
      const { method } = action.payload;

      eventBus.emit(
        EventFactory.paymentFormReady(200, {
          redirectContainer: method.redirectContainer,
        }),
      );
    }

    if (
      action.type === setPaymentInputs.toString() ||
      action.type === setTermsAgreementCheckbox.toString()
    ) {
      const {
        accounts: { activeAccountOption },
        customerInformation: { termsAgreement },
        paymentForm: { paymentInputs },
        paymentMethods: {
          paymentMethod: { preprocessor },
        },
      } = store.getState();

      const fieldsInformation = Object.entries(paymentInputs).reduce(
        (acc, [name, field]: [name: string, field: PaymentMethodInput]) => {
          if (name === 'accountId') {
            acc.push({
              name,
              value: activeAccountOption.value,
              isValid: Boolean(activeAccountOption.value),
            });
          } else {
            // todo check amount field on favorite amounts
            acc.push({
              name,
              value: field.value || null,
              isValid: field.isValid || false,
            });
          }

          return acc;
        },
        [],
      );

      const agreementCheckbox = termsAgreement.isCheckboxVisible
        ? [
            {
              name: 'agreementCheckbox',
              value: termsAgreement.isChecked,
              isValid: termsAgreement.isChecked,
            },
          ]
        : [];

      eventBus.emit(
        EventFactory.paymentAttributesChanged(
          PreProcessorAttributeFilter.filter(
            [...fieldsInformation, ...agreementCheckbox],
            preprocessor,
          ),
        ),
      );
    }

    if (
      action.type === changeConfirmationFieldValue.toString() ||
      action.type === setConfirmationFormData.toString()
    ) {
      const {
        paymentConfirmation: { confirmationData },
      } = store.getState();

      eventBus.emit(
        EventFactory.paymentAttributesChanged(Object.values(confirmationData)),
      );
    }

    if (action.type === fetchPaymentInfoSuccess.toString()) {
      const resultAction = paymentProcessingResult.resultAction;
      if (
        resultAction?.type === ResultActionTypes.DISPLAY ||
        PaymentStateChecker.isSuccess(activeType, action.payload.state)
      ) {
        eventBus.emit(EventFactory.paymentProcessed(200, {}));
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }

      if (PaymentStateChecker.isFailed(activeType, action.payload.state)) {
        eventBus.emit(EventFactory.paymentProcessed(500, {}));
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }
    }

    if (action.type === processPaymentFailure.toString()) {
      eventBus.emit(EventFactory.paymentProcessed(500, {}));
      eventBus.emit(EventFactory.paymentAttributesChanged([]));
    }

    if (action.type === setActiveScreen.toString()) {
      if (action.payload === DynamicScreens.ConfirmationForm) {
        //@TODO: we do not need too much events here
        eventBus.emit(EventFactory.paymentProcessed(200, {}));
        eventBus.emit(EventFactory.paymentDataConfirmationInitiated());
      }

      if (
        action.payload === DynamicScreens.ProcessingResult ||
        action.payload === DynamicScreens.FinalScreen
      ) {
        eventBus.emit(EventFactory.paymentProcessingStarted());
      }
    }

    if (action.type === registrationPaymentSuccess.toString()) {
      if (
        paymentRegistrationResultAction &&
        paymentRegistrationResultAction.type === ResultActionTypes.DISPLAY
      ) {
        eventBus.emit(EventFactory.paymentProcessed(200, {}));
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }
    }

    if (action.type === registrationPaymentFailure.toString()) {
      eventBus.emit(EventFactory.paymentProcessed(500, {}));
      eventBus.emit(EventFactory.paymentAttributesChanged([]));
    }

    return result;
  };
